/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetTransactionsOptions,
  GetTransactionsQuery,
  GetTransactionsStatus200,
  GetTransactionsStatus400,
  GetTransactionsStatus500,
} from '../types/GetTransactions'
import type {
  InfiniteData,
  QueryKey,
  QueryClient,
  InfiniteQueryObserverOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'
import { getTransactions } from '../clients/getTransactions'
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'

export const getTransactionsInfiniteQueryKey = ({
  query,
}: Omit<GetTransactionsOptions, 'headers'> = {}) =>
  [{ url: '/v1/transactions', infinite: true }, ...(query ? [query] : [])] as const

type GetTransactionsInfiniteQueryKey = ReturnType<typeof getTransactionsInfiniteQueryKey>

export function getTransactionsInfiniteQueryOptions(
  { query }: GetTransactionsOptions = {},
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getTransactionsInfiniteQueryKey({ query })
  return infiniteQueryOptions<
    GetTransactionsStatus200,
    ResponseErrorConfig<GetTransactionsStatus400 | GetTransactionsStatus500>,
    InfiniteData<GetTransactionsStatus200>,
    typeof queryKey,
    NonNullable<GetTransactionsQuery['page']>
  >({
    queryKey,
    queryFn: async ({ signal, pageParam }) => {
      query = {
        ...(query ?? {}),
        ['page']: pageParam as unknown as GetTransactionsQuery['page'],
      } as GetTransactionsQuery
      return getTransactions({
        ...config,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      Array.isArray(lastPage) && lastPage.length === 0 ? undefined : lastPageParam + 1,
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
      firstPageParam <= 1 ? undefined : firstPageParam - 1,
  })
}

/**
 * @description Role-based date window: public callers are limited to the last month; authenticated callers may query up to the last six months. If `start` is omitted it defaults to the role-based minimum. If credentials are supplied but invalid, the request is rejected as unauthorized.
 * @summary Get a filtered, paginated list of transactions
 * {@link /v1/transactions}
 */
export function useGetTransactionsInfinite<
  TQueryFnData = GetTransactionsStatus200,
  TError = ResponseErrorConfig<GetTransactionsStatus400 | GetTransactionsStatus500>,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = GetTransactionsInfiniteQueryKey,
  TPageParam = NonNullable<GetTransactionsQuery['page']>,
>(
  {
    query,
  }: { query?: GetTransactionsOptions['query'] | (() => GetTransactionsOptions['query']) } = {},
  options: {
    query?: Partial<
      InfiniteQueryObserverOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = { query: typeof query === 'function' ? query() : query }
  const queryKey = resolvedOptions?.queryKey ?? getTransactionsInfiniteQueryKey(resolvedParams)

  const queryResult = useInfiniteQuery(
    {
      ...getTransactionsInfiniteQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as InfiniteQueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
    queryClient,
  ) as UseInfiniteQueryResult<TData, TError> & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
